import { Popover, Button, Icon } from '@nimbus-ds/components';
import { EllipsisIcon } from '@nimbus-ds/icons';
import { MenuButton } from '@nimbus-ds/patterns';
import { Status as StatusEnum, Country } from '@tiendanube/common/src/enums';
import Stack from 'commons/components/Stack';
import {
  useGetCountry,
  useHasPermission,
  useHasUnifiedShippingDocumentsEnabled,
} from 'domains/Auth/hooks';
import {
  DocumentType,
  FULFILLMENT_ORDER_ID_IDENTIFIER,
} from 'domains/FulfillmentOrders/pages/PrintDocumentPage/constants';
import useTranslationFulfillmentOrders from 'domains/FulfillmentOrders/useTranslationFulfillmentOrders';
import { NE_PRINT_LABEL_SOURCE } from 'domains/NuvemEnvioCommons/constants';
import useNuvemEnvioPrintLabel from 'domains/NuvemEnvioCommons/Orders/hooks/useNuvemEnvioPrintLabel/useNuvemEnvioPrintLabel';
import { trackingNuvemEnvioPrintLabelsOrderDetails } from 'domains/NuvemEnvioCommons/tracking';
import { isShippingMethodOca } from '../../../../../Orders/Orders/pages/OrderDetailsPage/components/EditDeliveryAdressModal/utils';
import { useGoToPrintPage } from '../../hooks';
import { trackingPrintDocument } from '../tracking';

interface PrintManagerDrodownProps {
  fulfillmentOrderId?: string;
  orderId: string;
  shipping: string;
  hasGeneratedTag: boolean;
  isNuvemEnvio?: boolean;
  paymentStatus?: StatusEnum;
}

function PrintManagerDropdown({
  fulfillmentOrderId,
  orderId,
  shipping,
  hasGeneratedTag,
  isNuvemEnvio,
  paymentStatus,
}: Readonly<PrintManagerDrodownProps>): JSX.Element {
  const t = useTranslationFulfillmentOrders();
  const storeCountry = useGetCountry();
  const { goToPrintPage } = useGoToPrintPage();
  const hasPrintShippingLabelPermission = useHasPermission(
    'print_order_shipping_labels',
  );
  const { printNuvemEnvioLabels } = useNuvemEnvioPrintLabel();
  const isUnifiedShippingDocumentsEnabled =
    useHasUnifiedShippingDocumentsEnabled();

  const isOca = isShippingMethodOca(shipping);
  const isOcaAndNotGeneratedTag = isOca && !hasGeneratedTag;

  const handlePrintManager = (documentType: DocumentType) => {
    if (documentType === DocumentType.NUVEM_ENVIO_LABEL && fulfillmentOrderId) {
      trackingNuvemEnvioPrintLabelsOrderDetails(orderId);
      printNuvemEnvioLabels(
        NE_PRINT_LABEL_SOURCE.ORDER_DETAILS,
        [fulfillmentOrderId],
        false,
        paymentStatus,
      );
    } else {
      goToPrintPage({
        ids: fulfillmentOrderId
          ? isOca
            ? [orderId]
            : [`${FULFILLMENT_ORDER_ID_IDENTIFIER}${fulfillmentOrderId}`]
          : [orderId],
        type: documentType,
        hasOca: isOca && documentType === DocumentType.LABEL,
        hasOcaAndNotGeneratedTag:
          isOcaAndNotGeneratedTag && documentType === DocumentType.LABEL,
      });
    }

    trackingPrintDocument(documentType, [orderId]);
  };

  return (
    <Popover
      id={`${fulfillmentOrderId ?? orderId}-printlabelDetail`}
      position="bottom-start"
      padding="small"
      arrow={false}
      enabledClick
      content={
        <Stack column spacing="tight" align="flex-start">
          {isUnifiedShippingDocumentsEnabled ? (
            <>
              {hasPrintShippingLabelPermission && (
                <>
                  {isOca && (
                    <MenuButton
                      label={t(`printManager.ocaLabel.menuTitle`)}
                      onClick={() => handlePrintManager(DocumentType.LABEL)}
                    />
                  )}
                  <MenuButton
                    label={t(`printManager.shippingDocument.option`)}
                    onClick={() =>
                      handlePrintManager(DocumentType.SHIPPING_DOCUMENT)
                    }
                  />
                </>
              )}
              {storeCountry === Country.BR && (
                <MenuButton
                  label={t(`printManager.contentDeclaration.title`)}
                  onClick={() =>
                    handlePrintManager(DocumentType.CONTENT_DECLARATION)
                  }
                />
              )}
            </>
          ) : (
            <>
              {hasPrintShippingLabelPermission && (
                <>
                  <MenuButton
                    label={t(`printManager.label.title`)}
                    onClick={() => handlePrintManager(DocumentType.LABEL)}
                  />

                  {isNuvemEnvio && (
                    <MenuButton
                      label={t(`printManager.nuvemEnvio.title`)}
                      onClick={() =>
                        handlePrintManager(DocumentType.NUVEM_ENVIO_LABEL)
                      }
                    />
                  )}
                </>
              )}
              {storeCountry === Country.BR && (
                <MenuButton
                  label={t(`printManager.contentDeclaration.title`)}
                  onClick={() =>
                    handlePrintManager(DocumentType.CONTENT_DECLARATION)
                  }
                />
              )}
              <MenuButton
                label={t(`printManager.receipt.title`)}
                onClick={() => handlePrintManager(DocumentType.RECEIPT)}
              />
            </>
          )}
        </Stack>
      }
    >
      <Button appearance="neutral">
        <Icon source={<EllipsisIcon />} color="primary-textLow" />
        {t('printManager.dropdown')}
      </Button>
    </Popover>
  );
}

export default PrintManagerDropdown;
